import { useState } from 'react'
import { CheckIcon, SearchIcon, StoreIcon, TicketIcon } from 'lucide-react'

import { cn } from 'cn'
import { Input } from '@ems/ui/components/input'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { Field } from '@ems/ui/patterns/field'
import { formatMoney } from '@ems/ui/lib/format'

import { OptionSelect } from '#admin/components/option-select.tsx'
import { DeskSection } from '#admin/features/exhibitor/onboarding/desk-section.tsx'
import { passQuota, type Stand } from '#admin/features/exhibitor/onboarding/draft.ts'

/**
 * The free stands, as cards to select. Each card shows what decides the price and the passes:
 * the hall, the area, the rate and the passes the stand type gives.
 */
export function StandPickerSection({
  stands,
  chosen,
  onToggle,
  currency,
  currencies,
  onCurrency,
  currencyLocked,
  error,
  locked,
  title = 'Stands',
  description = 'Select one or more free stands. The stand type sets the rate and the passes.',
}: {
  /** The free stands in the chosen currency. */
  stands: readonly Stand[]
  chosen: ReadonlySet<string>
  onToggle: (id: string) => void
  currency: string
  currencies: readonly string[]
  onCurrency: (currency: string) => void
  /** The application has its currency once it is open. */
  currencyLocked: boolean
  error: string | undefined
  locked: boolean
  /** The sponsor desk names the stands as the sponsor's allocation. */
  title?: string
  description?: string
}) {
  const [search, setSearch] = useState('')
  const needle = search.trim().toLowerCase()
  const shown =
    needle === ''
      ? stands
      : stands.filter((stand) =>
          `${stand.code} ${stand.hall ?? ''} ${stand.type.name}`.toLowerCase().includes(needle),
        )
  const picked = stands.filter((stand) => chosen.has(stand.id))
  const area = picked.reduce((sum, stand) => sum + Number(stand.areaSqm), 0)

  return (
    <DeskSection
      number={2}
      title={title}
      description={description}
      done={locked}
      aside={
        currencies.length > 1 ? (
          <OptionSelect
            size="sm"
            aria-label="Currency"
            className="w-28"
            value={currency}
            disabled={currencyLocked}
            onValueChange={onCurrency}
            options={currencies.map((code) => ({ value: code, label: code }))}
          />
        ) : null
      }
    >
      {stands.length === 0 ? (
        <EmptyState
          icon={<StoreIcon />}
          title="No stand is free"
          description={`No free stand is sold in ${currency}. Add stands on the floor plan.`}
        />
      ) : (
        <>
          <Field label={<span className="sr-only">Find a stand</span>}>
            {(props) => (
              <div className="relative">
                <SearchIcon
                  aria-hidden="true"
                  className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2"
                />
                <Input
                  {...props}
                  type="search"
                  placeholder="Stand code, hall or type"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="pl-9"
                />
              </div>
            )}
          </Field>

          {shown.length === 0 ? (
            <p className="text-muted-foreground text-sm">No free stand has this code or hall.</p>
          ) : (
            <ul className="grid max-h-104 gap-2 overflow-y-auto p-0.5 sm:grid-cols-2 xl:grid-cols-3">
              {shown.map((stand) => (
                <li key={stand.id}>
                  <StandCard
                    stand={stand}
                    selected={chosen.has(stand.id)}
                    disabled={locked}
                    onToggle={() => onToggle(stand.id)}
                  />
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <p
        role="status"
        className={cn(
          'text-sm',
          error === undefined ? 'text-muted-foreground' : 'text-destructive',
        )}
      >
        {picked.length === 0
          ? (error ?? 'No stand is selected.')
          : `${picked.length} ${picked.length === 1 ? 'stand' : 'stands'} · ${
              Math.round(area * 100) / 100
            } m² · ${passQuota(picked)} convention passes`}
      </p>
    </DeskSection>
  )
}

function StandCard({
  stand,
  selected,
  disabled,
  onToggle,
}: {
  stand: Stand
  selected: boolean
  disabled: boolean
  onToggle: () => void
}) {
  const price = Number(stand.areaSqm) * stand.ratePerSqm
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onToggle}
      className={cn(
        'flex w-full flex-col gap-2 rounded-lg border p-3 text-left text-sm transition-colors',
        'focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-60',
        selected
          ? 'border-primary bg-primary/5 ring-primary/30 ring-1'
          : 'hover:bg-muted/50 border-(--border)',
      )}
    >
      <span className="flex items-center justify-between gap-2">
        <span className="font-mono text-base font-semibold">{stand.code}</span>
        <span
          aria-hidden="true"
          className={cn(
            'flex size-5 items-center justify-center rounded-full border',
            selected ? 'border-primary bg-primary text-primary-foreground' : 'border-(--border)',
          )}
        >
          {selected ? <CheckIcon className="size-3.5" /> : null}
        </span>
      </span>
      <span className="text-muted-foreground text-xs">
        {[stand.hall ?? 'No hall', stand.type.name, stand.isPremium ? 'Premium' : null]
          .filter((part) => part !== null)
          .join(' · ')}
      </span>
      <span className="flex items-end justify-between gap-2">
        <span className="text-muted-foreground text-xs tabular-nums">
          {`${stand.areaSqm} m² × ${formatMoney(stand.ratePerSqm, stand.type.currency)}`}
          <span className="mt-0.5 flex items-center gap-1">
            <TicketIcon aria-hidden="true" className="size-3.5" />
            {`${stand.type.conventionPasses} ${stand.type.conventionPasses === 1 ? 'pass' : 'passes'}`}
          </span>
        </span>
        <span className="font-medium tabular-nums">{formatMoney(price, stand.type.currency)}</span>
      </span>
    </button>
  )
}
